import Masonry from 'react-masonry-css'
import { useState, useEffect, useRef } from "react";
import React from 'react';
import { useGlobal } from "../contexts/GlobalContext";

function Gallery() {
  const { iUrl } = useGlobal();
  const [galleryImages, setGalleryImages] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const galleryTitleRef = useRef(null);

  useEffect(() => {
    const fetchGallery = async () => {
      if (hasLoaded) return; // Prevent multiple fetches

      try {
        setIsLoading(true);
        setHasLoaded(true);
        const [galleryFetch] = await Promise.all([
          fetch(`${import.meta.env.VITE_DIRECTUS_PUBLIC_URL}/items/gallery_masonry_files?filter[gallery_masonry_id][_eq]=1`),
        ]);
        const gallery = await galleryFetch.json();

        setGalleryImages(gallery);
        // Trigger animation after data is set
        setTimeout(() => setShowImages(true), 100);
      } catch (error) {
        console.error("Content fetch error:", error);
        setHasLoaded(false); // Allow retry on error
      } finally {
        setIsLoading(false);
      }
    };

    // Intersection Observer setup
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasLoaded) {
            fetchGallery();
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the heading is visible
        rootMargin: '50px' // Start loading 50px before heading comes into view
      }
    );

    // Start observing the gallery title
    if (galleryTitleRef.current) {
      observer.observe(galleryTitleRef.current);
    }

    // Cleanup
    return () => {
      if (galleryTitleRef.current) {
        observer.unobserve(galleryTitleRef.current);
      }
    };
  }, [hasLoaded]);

  const breakpointColumns = {
    default: 4,
    1100: 3,
    700: 2,
    500: 2
  }

  return (
    <section className="gallery py-16">
      <div className="gallery__container max-w-content mx-auto px-2 lg:px-16">
        {/* Section Title with ref for Intersection Observer */}
        <h2
          ref={galleryTitleRef}
          className="gallery__title font-header text-4xl lg:text-5xl text-secondary-700 text-center mb-12"
        >
          Gallery
        </h2>

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-secondary-700"></div>
            <p className="mt-2 text-secondary-700">Loading gallery...</p>
          </div>
        )}

        {/* Masonry Grid */}
        {galleryImages.data && !isLoading && (
          <Masonry
            breakpointCols={breakpointColumns}
            className={`gallery__masonry-grid flex -ml-4 transition-all duration-2000 ease-out ${
              showImages ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            columnClassName="gallery__masonry-column pl-4"
          >
            {galleryImages.data.map((image, index) => (
              <div
                key={image.id}
                className={`gallery__item mb-4 transition-all duration-2000 ease-out ${
                  showImages ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{
                  transitionDelay: `${index * 100}ms`
                }}
              >
                <img
                  src={`${iUrl}${image.directus_files_id}?format=webp`}
                  alt={image.alt || 'Gallery image'}
                  className="gallery__image w-full object-cover shadow-md hover:shadow-lg transition-shadow duration-300"
                  loading="lazy"
                />
              </div>
            ))}
          </Masonry>
        )}

        {/* Empty State */}
        {!isLoading && !hasLoaded && (
          <div className="text-center py-8 text-secondary-700">
            Scroll down to load gallery...
          </div>
        )}
      </div>
    </section>
  )
}

export default Gallery
